import { ShieldCheck, Award, ClipboardCheck, FileText, ChevronDown, Download } from 'lucide-react';
import { Page, Reveal, MaskedLines } from '../components/motion';
import { CTASection } from '../components/CTASection';
import { REPUTATION } from '../data/content';
import { POLICY_DOCS } from '../data/policies';
import { useSEO } from '../hooks/useSEO';

const SECTIONS = [
  {
    id: 'health-safety',
    number: '01',
    icon: ShieldCheck,
    title: 'Health & Safety',
    body: [
      'At EhimoBCE, we adhere to safety measures on every project we undertake.',
      'Every project is delivered within the expected health & safety requirements — protecting our people, our clients and the public on and around our sites.',
    ],
  },
  {
    id: 'quality-delivery',
    number: '02',
    icon: ClipboardCheck,
    title: 'Quality & Delivery Commitments',
    body: [
      'Our reputation is built on a simple set of commitments that every project is measured against:',
    ],
    list: REPUTATION,
  },
  {
    id: 'professional-standards',
    number: '03',
    icon: Award,
    title: 'Professional Standards',
    body: [
      'EhimoBCE is led by a foremost builder, registered with the Council of Registered Builders of Nigeria (CORBON) and a Fellow of the Nigerian Institute of Building (NIOB) — Bldr. Lynus Aiteobhor, FNIOB.',
      'Our team includes a registered builder (FNIOB), an architect and a civil engineer, delivering projects with modern construction management skills and tools.',
    ],
  },
];

export default function Policies() {
  useSEO('Policies', 'Ehimo Building & Civil Engineering policies — health & safety, quality and delivery commitments, and professional standards (CORBON, NIOB).');
  return (
    <Page>
      <section className="pt-40 pb-20 lg:pt-52 lg:pb-28 border-b border-line relative overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Reveal y={14} className="mb-6">
            <p className="text-xs uppercase tracking-[0.3em] text-accent">Policies</p>
          </Reveal>
          <MaskedLines
            lines={['Standards we', 'build by.']}
            delay={0.1}
            lineClassName="font-display text-3xl sm:text-5xl lg:text-6xl font-semibold uppercase tracking-tight leading-[0.95]"
          />
        </div>
      </section>

      <section className="bg-paper text-ink">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-28 border border-paperline divide-y divide-paperline bg-white" data-testid="policies-nav">
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                data-testid={`policy-nav-${s.id}`}
                className="flex items-center gap-4 px-6 py-5 group hover:bg-paper transition-colors"
              >
                <span className="font-display text-sm text-ink/50">{s.number}</span>
                <span className="text-sm uppercase tracking-[0.15em] text-ink/80">
                  {s.title}
                </span>
              </a>
            ))}
            <a
              href="#policy-documents"
              data-testid="policy-nav-policy-documents"
              className="flex items-center gap-4 px-6 py-5 group hover:bg-paper transition-colors"
            >
              <span className="font-display text-sm text-ink/50">04</span>
              <span className="text-sm uppercase tracking-[0.15em] text-ink/80">Policy Documents</span>
            </a>
          </div>
        </aside>

        <div className="lg:col-span-8 space-y-20">
          {SECTIONS.map((s) => (
            <article key={s.id} id={s.id} data-testid={`policy-section-${s.id}`} className="scroll-mt-28">
              <Reveal className="flex items-center gap-5 mb-8">
                <div className="w-14 h-14 bg-accent flex items-center justify-center shrink-0">
                  <s.icon size={24} className="text-ink" />
                </div>
                <h2 className="font-display text-2xl lg:text-4xl font-semibold uppercase tracking-tight text-ink">{s.title}</h2>
              </Reveal>
              <Reveal delay={0.1} className="space-y-5 max-w-2xl">
                {s.body.map((p, i) => (
                  <p key={i} className="text-inkmute leading-relaxed">{p}</p>
                ))}
                {s.list && (
                  <ul className="mt-6 border border-paperline divide-y divide-paperline bg-white">
                    {s.list.map((item) => (
                      <li key={item} className="px-6 py-4 text-sm text-ink/85 flex gap-3">
                        <span className="w-1.5 h-1.5 bg-accent mt-2 shrink-0" aria-hidden="true" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </Reveal>
            </article>
          ))}

          <article id="policy-documents" data-testid="policy-documents" className="scroll-mt-28">
            <Reveal className="flex items-center gap-5 mb-8">
              <div className="w-14 h-14 bg-accent flex items-center justify-center shrink-0">
                <FileText size={24} className="text-ink" />
              </div>
              <h2 className="font-display text-2xl lg:text-4xl font-semibold uppercase tracking-tight text-ink">Policy Documents</h2>
            </Reveal>
            <Reveal delay={0.1} className="max-w-2xl mb-8">
              <p className="text-inkmute leading-relaxed">
                The full text of our company policy documents, as signed by the Chief Executive Officer.
              </p>
            </Reveal>
            <div className="space-y-4">
              {POLICY_DOCS.map((doc) => (
                <details key={doc.id} data-testid={`policy-doc-${doc.id}`} className="group border border-paperline bg-white">
                  <summary className="flex items-center justify-between gap-4 px-6 py-5 cursor-pointer list-none select-none">
                    <span className="flex items-center gap-4">
                      <span className="w-10 h-10 bg-accent flex items-center justify-center shrink-0">
                        <FileText size={18} className="text-ink" />
                      </span>
                      <span>
                        <span className="block font-display text-base lg:text-lg font-medium uppercase tracking-tight text-ink">
                          {doc.title}
                        </span>
                        <span className="block text-[10px] uppercase tracking-[0.2em] text-inkmute mt-1">
                          Signed — {doc.date}
                        </span>
                      </span>
                    </span>
                    <span className="flex items-center gap-4 shrink-0">
                      <a
                        href={`/policies/${doc.id}.pdf`}
                        download
                        data-testid={`policy-download-${doc.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="hidden sm:inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-ink border border-paperline px-4 py-2 hover:bg-accent hover:border-accent transition-colors"
                      >
                        <Download size={13} /> PDF
                      </a>
                      <ChevronDown size={18} className="text-ink/40 transition-transform duration-300 group-open:rotate-180" />
                    </span>
                  </summary>
                  <div className="px-6 pb-8 pt-2 border-t border-paperline">
                    {doc.blocks.map((b, i) => (
                      <div key={i} className="mt-6">
                        {b.heading && (
                          <h3 className="font-display text-sm uppercase tracking-[0.15em] text-ink mb-3">{b.heading}</h3>
                        )}
                        {b.paragraphs.map((p, j) => (
                          <p key={j} className="text-sm text-inkmute leading-relaxed mb-3">{p}</p>
                        ))}
                        {b.list && (
                          <ul className="space-y-2 mt-3">
                            {b.list.map((item) => (
                              <li key={item} className="text-sm text-ink/80 flex gap-3">
                                <span className="w-1.5 h-1.5 bg-accent mt-2 shrink-0" aria-hidden="true" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                    <div className="border-t border-paperline pt-5 mt-8 flex flex-wrap items-center justify-between gap-4">
                      <p className="text-xs uppercase tracking-[0.2em] text-ink">Signed — {doc.signed}</p>
                      <a
                        href={`/policies/${doc.id}.pdf`}
                        download
                        data-testid={`policy-download-bottom-${doc.id}`}
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink font-medium border-b-2 border-accent pb-1 hover:bg-accent transition-colors"
                      >
                        Download PDF <Download size={14} />
                      </a>
                    </div>
                  </div>
                </details>
              ))}
            </div>
          </article>
        </div>
      </div>
      </section>

      <CTASection title={['Work with a', 'team you can trust.']} />
    </Page>
  );
}
